<script setup>
import CalendarBlankIcon from '@iconify-vue/ph/calendar-blank';
import ListIcon from '@iconify-vue/ph/list';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { formatDate } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Client',
                }),
            cell: (props) =>
                h(LinkCell, {
                    text: props.row.original.name,
                    href: route('front.projectManagement.smo.posts', {
                        tenant: props.row.original.id,
                    }),
                    inertia: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'totalScheduled',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Programmées',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.totalScheduled,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'totalPublished',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Publiées',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.totalPublished,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'totalFailed',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'En échec',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.totalFailed,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'nextScheduledAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Prochaine publication',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.nextScheduledAt
                        ? formatDate(
                              props.row.original.nextScheduledAt,
                              false,
                              true,
                          )
                        : '-',
                }),
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                size: 'small',
                                title: 'Voir la liste des publications',
                                href: route(
                                    'front.projectManagement.smo.posts',
                                    {
                                        tenant: props.row.original.id,
                                    },
                                ),
                                icon: ListIcon,
                                square: true,
                            }),
                            h(ActionButton, {
                                size: 'small',
                                title: 'Voir le calendrier',
                                href: route(
                                    'front.projectManagement.smo.calendar',
                                    {
                                        tenant: props.row.original.id,
                                    },
                                ),
                                icon: CalendarBlankIcon,
                                square: true,
                            }),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.smo.postPosts'),
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    />
</template>
